import { ACESFilmicToneMapping, WebGPURenderer } from 'three/webgpu'
import { z } from 'zod'
import canvasJson from './canvas.json'

/**
 * D-802 (PERF L-E1): ask the browser for the high-performance (discrete) GPU. Without it a PC with integrated
 * graphics may run the game on the integrated GPU while the main GPU idles. Browsers may ignore the hint (Chrome on
 * Windows has historically followed the OS per-app graphics setting), so the adapter actually picked is logged at
 * boot and shown on the F3 line (`gpuName`).
 */
export const GPU_POWER_PREFERENCE = 'high-performance' as const

/** Longest GPU name on the F3 line (PERF L-E1); longer names end in '…'. */
export const GPU_LABEL_MAX = 32

/**
 * FG-2 (D-982): the WebGPU canvas is configured `alphaMode: 'opaque'` (three's `alpha: false`) instead of
 * `'premultiplied'`, so the browser composites it without blending. The post chain's final quad already writes alpha 1
 * (src/render/post/graph.ts) and the page under the canvas is black, so the picture is the same; with `alpha: false`
 * three also clears to alpha 1 and skips premultiplying the clear colour. WebGL2 keeps its context as it was.
 */
export const canvasConfigSchema = z.object({
  /** WebGPU: an opaque canvas (`alpha: false`). false: three's default premultiplied canvas. */
  opaqueWebGPU: z.boolean(),
})
export type CanvasConfig = z.infer<typeof canvasConfigSchema>

let canvasConfig: CanvasConfig | null = null

/** `canvas.json` (Zod), parsed once. */
export function loadCanvasConfig(): CanvasConfig {
  return (canvasConfig ??= canvasConfigSchema.parse(canvasJson))
}

export type GameRendererParameters = NonNullable<ConstructorParameters<typeof WebGPURenderer>[0]>

function hasWebGPU(): boolean {
  return typeof navigator !== 'undefined' && 'gpu' in navigator
}

/**
 * The game's renderer options. three r186's WebGPU backend passes `powerPreference` to `navigator.gpu.requestAdapter`,
 * but its WebGL2 backend does not put it into the context attributes, so on the WebGL2 path (`?renderer=webgl2`, or no
 * `navigator.gpu`) the context is made here with three's own attributes plus the preference. three makes it with
 * `antialias: renderer.currentSamples > 0`, which is 0 at init (the output colour-space pass draws into a framebuffer
 * target that holds the MSAA), `alpha: true`, `depth: true`, `stencil: false`. A browser that has `navigator.gpu`
 * but no adapter still falls back inside three, without the preference. `opaque` (FG-2, default `canvas.json`) makes the
 * WebGPU canvas opaque; the WebGL2 path ignores it.
 */
export function rendererParameters(canvas: HTMLCanvasElement, forceWebGL: boolean, opaque: boolean = loadCanvasConfig().opaqueWebGPU): GameRendererParameters {
  const webgl = forceWebGL || !hasWebGPU()
  const base: GameRendererParameters = {
    canvas,
    antialias: true,
    reversedDepthBuffer: true,
    powerPreference: GPU_POWER_PREFERENCE,
    forceWebGL: webgl,
  }
  if (!webgl) return { ...base, alpha: !opaque }
  const context = canvas.getContext('webgl2', {
    antialias: false,
    alpha: true,
    depth: true,
    stencil: false,
    powerPreference: GPU_POWER_PREFERENCE,
  })
  return context === null ? base : { ...base, context }
}

/**
 * WebGPU with automatic WebGL2 fallback; `forceWebGL` backs `?renderer=webgl2`. `opaque`: see `rendererParameters`.
 *
 * A browser with `navigator.gpu` but no adapter falls back to WebGL2 inside three with `alpha: false`: three's WebGL2
 * backend still makes an alpha context (always, r186), and only clears to alpha 1 instead of 0.
 */
export async function createRenderer(canvas: HTMLCanvasElement, forceWebGL: boolean, opaque?: boolean): Promise<WebGPURenderer> {
  const renderer = new WebGPURenderer(rendererParameters(canvas, forceWebGL, opaque))
  await renderer.init()
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setSize(window.innerWidth, window.innerHeight, false)
  renderer.toneMapping = ACESFilmicToneMapping
  return renderer
}

export function backendName(renderer: WebGPURenderer): 'webgpu' | 'webgl2' {
  return (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true ? 'webgpu' : 'webgl2'
}

/** The fields of a WebGPU `GPUAdapterInfo` the readout uses (the project has no @webgpu/types). */
export interface AdapterInfoLike {
  vendor?: string
  architecture?: string
  device?: string
  description?: string
  isFallbackAdapter?: boolean
}

/** A WebGPU adapter's name: its description, else vendor, architecture and device; '(software)' for a fallback adapter. */
export function formatAdapterInfo(info: AdapterInfoLike | null | undefined): string {
  if (info === null || info === undefined) return 'unknown'
  const description = info.description?.trim() ?? ''
  const joined =
    description !== ''
      ? description
      : [info.vendor, info.architecture, info.device].filter((s): s is string => s !== undefined && s.trim() !== '').join(' ')
  const name = joined !== '' ? joined : 'unknown'
  return info.isFallbackAdapter === true ? `${name} (software)` : name
}

/** The parts of a WebGL2 context the readout uses. */
export type WebGLInfoSource = Pick<WebGL2RenderingContext, 'getExtension' | 'getParameter' | 'RENDERER'>

/** A WebGL2 context's GPU: the unmasked renderer when `WEBGL_debug_renderer_info` exists, else `RENDERER`. */
export function webglRendererName(gl: WebGLInfoSource): string {
  try {
    const ext = gl.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_RENDERER_WEBGL: number } | null
    const value: unknown = ext !== null ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : 'unknown'
  } catch {
    return 'unknown'
  }
}

/**
 * The GPU the renderer runs on (PERF L-E1), read once after `init()`: WebGPU's `device.adapterInfo`, or the WebGL2
 * renderer string. Never per frame.
 */
export function gpuName(renderer: Pick<WebGPURenderer, 'backend'>): string {
  const backend = renderer.backend as {
    isWebGPUBackend?: boolean
    device?: { adapterInfo?: AdapterInfoLike } | null
    gl?: WebGLInfoSource
  }
  if (backend.isWebGPUBackend === true) return formatAdapterInfo(backend.device?.adapterInfo)
  return backend.gl !== undefined ? webglRendererName(backend.gl) : 'unknown'
}

/**
 * The F3 GPU label: an ANGLE string ('ANGLE (NVIDIA, NVIDIA GeForce RTX 3080 (0x00002206) Direct3D11 vs_5_0 ps_5_0,
 * D3D11)') keeps the device name only, then anything over `max` characters ends in '…'.
 */
export function shortGpuName(name: string, max: number = GPU_LABEL_MAX): string {
  let s = name
  const angle = /^ANGLE \([^,]*, (.+), [^,]*\)$/.exec(s)
  if (angle !== null) s = angle[1].replace(/ \(0x[0-9a-fA-F]+\)/, '').replace(/ (Direct3D|OpenGL|Vulkan|Metal).*$/, '')
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s
}
